import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { ChevronRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

// Import sector-specific images
import sectorEcommerce from "@/assets/sector-ecommerce.png";
import sectorBanking from "@/assets/sector-banking.png";
import sectorHealthcare from "@/assets/sector-healthcare.png";
import sectorGovernment from "@/assets/sector-government.png";
import sectorTelecom from "@/assets/sector-telecom.png";
import sectorTransport from "@/assets/sector-transport.png";

interface SectorInfo {
  image: string;
  key: string;
}

const WhoSection = () => {
  const { t } = useLanguage();
  const { ref, isVisible } = useScrollReveal();
  const [selectedSector, setSelectedSector] = useState<string | null>(null);

  const sectors: SectorInfo[] = [
    { image: sectorEcommerce, key: 'ecommerce' },
    { image: sectorBanking, key: 'banking' },
    { image: sectorHealthcare, key: 'healthcare' },
    { image: sectorGovernment, key: 'government' },
    { image: sectorTelecom, key: 'telecom' },
    { image: sectorTransport, key: 'transport' },
  ];

  const selectedSectorData = sectors.find(s => s.key === selectedSector);

  return (
    <section className="py-20 bg-muted/30" aria-labelledby="who-title">
      <div 
        ref={ref as React.RefObject<HTMLDivElement>}
        className={`container mx-auto px-4 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-4xl mx-auto text-center mb-12">
          <h2 id="who-title" className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {t('who.title')}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('who.description')}
          </p>
        </div>

        {/* Statistics - more engaging */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-16">
          <div className="text-center p-6 bg-card rounded-2xl border border-border/50 hover:border-primary/30 transition-colors">
            <span className="block text-4xl md:text-5xl font-bold text-primary mb-2">
              {t('who.stats.nl.percentage')}
            </span>
            <span className="text-muted-foreground text-sm leading-relaxed">
              {t('who.stats.nl.label')}
            </span>
          </div>
          <div className="text-center p-6 bg-card rounded-2xl border border-border/50 hover:border-primary/30 transition-colors">
            <span className="block text-4xl md:text-5xl font-bold text-primary mb-2">
              {t('who.stats.eu.percentage')}
            </span>
            <span className="text-muted-foreground text-sm leading-relaxed">
              {t('who.stats.eu.label')}
            </span>
          </div>
          <div className="text-center p-6 bg-card rounded-2xl border border-border/50 hover:border-primary/30 transition-colors">
            <span className="block text-4xl md:text-5xl font-bold text-primary mb-2">
              {t('who.stats.websites.percentage')}
            </span>
            <span className="text-muted-foreground text-sm leading-relaxed">
              {t('who.stats.websites.label')}
            </span>
          </div>
        </div>

        {/* Sector cards with images */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 max-w-5xl mx-auto mb-12">
          {sectors.map((sector, index) => (
            <button 
              key={sector.key}
              onClick={() => setSelectedSector(sector.key)}
              className="group p-6 bg-card rounded-2xl border border-border/50 hover:border-primary/30 hover:shadow-xl transition-all duration-300 cursor-pointer text-left"
              style={{ animationDelay: `${index * 100}ms` }}
              aria-label={`${t(`who.sectors.${sector.key}`)} - ${t('who.moreInfo')}`}
            >
              <div className="flex flex-col items-center text-center gap-4">
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full border border-border/30 bg-muted/30 p-4 flex items-center justify-center overflow-hidden group-hover:border-primary/30 group-hover:bg-primary/5 transition-all duration-300">
                  <img 
                    src={sector.image} 
                    alt="" 
                    className="w-full h-full object-contain rounded-full group-hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <h3 className="font-semibold text-foreground text-sm md:text-base">
                  {t(`who.sectors.${sector.key}`)}
                </h3>
                <span className="text-xs text-primary flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {t('who.moreInfo')} <ChevronRight className="w-3 h-3" />
                </span>
              </div>
            </button>
          ))}
        </div>

      </div>

      {/* Sector Detail Dialog */}
      <Dialog open={!!selectedSector} onOpenChange={() => setSelectedSector(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <div className="flex items-center gap-4 mb-2">
              {selectedSectorData && (
                <div className="w-16 h-16 rounded-full border border-border/30 bg-muted/30 p-3 flex items-center justify-center overflow-hidden">
                  <img 
                    src={selectedSectorData.image} 
                    alt="" 
                    className="w-full h-full object-contain rounded-full"
                  />
                </div>
              )}
              <DialogTitle className="text-xl">
                {selectedSector && t(`who.sectors.${selectedSector}`)}
              </DialogTitle>
            </div>
            <DialogDescription asChild>
              <div className="text-left space-y-4 pt-4">
                <p className="text-muted-foreground">
                  {selectedSector && t(`who.sectors.${selectedSector}.intro`)}
                </p>
                <div>
                  <h4 className="font-semibold text-foreground mb-2">
                    {t('who.sectors.examples')}
                  </h4>
                  <ul className="space-y-2">
                    {selectedSector && [1, 2, 3].map((i) => (
                      <li key={i} className="flex items-start gap-2 text-muted-foreground">
                        <span className="text-primary mt-1">✓</span>
                        {t(`who.sectors.${selectedSector}.example${i}`)}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    {t('who.sectors.requirement')}
                  </p>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default WhoSection;
